// V1.9 — Centralized SEO copy + reusable JSON-LD schema blocks.
// All long-tail phrases live here so we can tune them in one place.

export const SITE_NAME = "FSRS — Fire Suppression Retrofit Studio";
export const SITE_URL = "https://fsr.studio";
export const SITE_LOGO = `${SITE_URL}/favicon.ico`;
export const SITE_TAGLINE =
    "AI fire suppression design assistant for NFPA 13 existing-building sprinkler retrofits — engineered for licensed PE review.";

export const HOME_TITLE = "FSR Studio | You’re the Expert - We’re the Shortcut.";
export const HOME_DESCRIPTION =
    "FSR Studio prepares preliminary fire sprinkler retrofit layouts from your drawings, ready for the licensed professional who reviews the work.";

export const PAGE_SEO = {
    home: {
        title: HOME_TITLE,
        description: HOME_DESCRIPTION,
        keywords:
            "florida high-rise fire suppression retrofit, nfpa 13 existing building retrofit, ai fire suppression design assistant, how to retrofit existing building with fire sprinklers, florida fire code retrofit deadline 2026, high-rise condo fire code compliance florida",
        canonical: "/",
    },
    pricing: {
        title:
            "FSRS Pricing | Founding Beta $249/month | Professional & Firm Plans",
        description:
            "Founding Beta is $249/month and includes 15 SRUs each month. Starter is $99/month. Professional and Firm plans are coming soon.",
        keywords:
            "ai fire suppression retrofit software pricing, nfpa 13 existing building retrofit cost, florida high-rise fire suppression retrofit, founding beta $249, professional plan, firm plan",
        canonical: "/pricing",
    },
    howItWorks: {
        title:
            "How FSRS Works | From Existing Drawings to NFPA 13 Retrofit Ideas",
        description:
            "Upload your building drawings and get AI-generated hazard classification, 3D layout suggestions, and hydraulic summaries. Designed as a professional assistant for licensed fire protection engineers. Outputs require PE review.",
        keywords:
            "how to retrofit existing building with fire sprinklers, nfpa 13 existing building retrofit workflow, ai fire suppression design assistant, florida high-rise fire suppression retrofit, hazard classification ai",
        canonical: "/how-it-works",
    },
    examples: {
        title: "Fictional Florida examples | FSRS",
        description:
            "Three fictional Florida plan examples: a dental office, strip retail, and a warehouse flex. Example overlays only, with budgetary ranges for a professional to confirm.",
        keywords:
            "nfpa 13 retrofit examples, fictional florida fire sprinkler examples, dental office sprinkler layout, strip retail sprinkler layout, warehouse flex sprinkler layout",
        canonical: "/examples",
    },
    testimonials: {
        title:
            "Testimonials · Florida Fire Protection Engineers Using FSRS | FSRS",
        description:
            "Hear from licensed Florida fire protection engineers using the AI fire suppression design assistant for high-rise condo fire code compliance and NFPA 13 existing-building sprinkler retrofit projects.",
        canonical: "/testimonials",
    },
    resources: {
        title:
            "FSRS Resources | Florida Fire Code Retrofit Guides & Checklists",
        description:
            "Free resources for fire protection engineers: 2026 Florida high-rise retrofit checklists, NFPA 13 guides, and AI workflow best practices.",
        keywords:
            "florida fire code retrofit deadline 2026, florida high-rise retrofit checklist, nfpa 13 existing building retrofit guide, sb 4-d compliance, florida fire prevention code",
        canonical: "/resources",
    },
};

// ----- Reusable JSON-LD schema generators -----

const PUBLISHED_OFFERS = [
    {
        "@type": "Offer",
        name: "Starter",
        price: "99.00",
        priceCurrency: "USD",
        category: "Subscription",
    },
    {
        "@type": "Offer",
        name: "Founding Beta",
        price: "249.00",
        priceCurrency: "USD",
        category: "Subscription",
    },
];

const UNRELEASED_COMING_SOON_OFFERS = [
    {
        "@type": "Offer",
        name: "Professional",
        availability: "https://schema.org/PreOrder",
        description: "Coming soon",
        category: "Subscription",
    },
    {
        "@type": "Offer",
        name: "Firm Plan",
        availability: "https://schema.org/PreOrder",
        description: "Coming soon",
        category: "Subscription",
    },
];

export function softwareApplicationSchema() {
    return {
        id: "ld-softwareapplication",
        data: {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "FSRS — Fire Suppression Retrofit Studio",
            applicationCategory: "BusinessApplication",
            applicationSubCategory:
                "AI fire suppression design assistant for NFPA 13 existing-building sprinkler retrofit",
            operatingSystem: "Web",
            url: SITE_URL,
            description: SITE_TAGLINE,
            offers: [...PUBLISHED_OFFERS, ...UNRELEASED_COMING_SOON_OFFERS],
            featureList: [
                "AI hazard classification (NFPA 13)",
                "3D sprinkler layout drafting",
                "Hydraulic summary previews",
                "Watermarked PDF / DXF / IFC exports",
                "Florida SB 4-D structural-inspection context and Florida Fire Prevention Code sprinkler context",
            ],
            audience: {
                "@type": "Audience",
                audienceType:
                    "Licensed Fire Protection Engineers (FPE), NICET-certified designers, fire-protection firms",
            },
        },
    };
}

export function organizationSchema() {
    return {
        id: "ld-organization",
        data: {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "FSRS — Fire Suppression Retrofit Studio",
            url: SITE_URL,
            logo: SITE_LOGO,
            slogan: SITE_TAGLINE,
            sameAs: [],
            contactPoint: [
                {
                    "@type": "ContactPoint",
                    contactType: "engineering inquiries",
                    email: "support@fsr.studio",
                    availableLanguage: ["English"],
                },
            ],
        },
    };
}

export function faqSchema(entries) {
    return {
        id: "ld-faq",
        data: {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: entries.map((q) => ({
                "@type": "Question",
                name: q.q,
                acceptedAnswer: { "@type": "Answer", text: q.a },
            })),
        },
    };
}

